/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ThemeSpaceVariable, showToast } from "@caspeco/casper-ui-library";
import queryClient from "../query-client";
import { useMutation } from "@tanstack/react-query";
import { User } from "../models/user";
import { updateUserAsync } from "../services/api/user-api";
import { QueryKeys } from "../types/query-key-enum";
import { ZodObject } from "zod";
import axios, { HttpStatusCode } from "axios";
import { getMarcException } from "../utils/general-helper";
import { useTranslation } from "react-i18next";
import { isEqual } from "lodash";
import { useAppContext } from "./use-app-context";
import applicationInsights from "../services/application-insights/application-insights";

type UpdateUserMutationContext = {
	previousUserSnapShot?: User;
	attemptedValues?: Partial<User>;
};

export const useUpdateUserQuery = (
	validationSchema: ZodObject,
): {
	saveUser: (values: Partial<User>) => void;
	saveUserAsync: (values: Partial<User>) => Promise<boolean>;
} => {
	const { t } = useTranslation();
	const { safeArea } = useAppContext();

	const updateUserCache = (values: Partial<User>) => {
		queryClient.setQueryData([QueryKeys.User], (previousUserValues: User | undefined) => {
			if (!previousUserValues) return;

			const updatedValues = { ...previousUserValues, ...values };
			return new User(updatedValues); // Assuming User class accepts an object to create a new instance
		});
	};

	const mutation = useMutation<HttpStatusCode, Error, Partial<User>, UpdateUserMutationContext>({
		mutationFn: updateUserAsync,
		onMutate: async (values) => {
			await queryClient.cancelQueries({ queryKey: [QueryKeys.User] });
			const previousUserSnapShot = queryClient.getQueryData<User>([QueryKeys.User]);

			updateUserCache(values);

			return { previousUserSnapShot, attemptedValues: values };
		},
		onError: (error, _, context) => {
			if (axios.isAxiosError(error)) {
				if (error.response?.data.code) {
					const marcError = getMarcException(error.response?.data.code, t);
					showToast({
						type: "error",
						title: marcError.title,
						description: marcError.description,
						duration: 5000,
						distanceToBottom: safeArea.bottomAsPx(),
					});
				} else {
					showToast({ type: "error", description: t("error_toast_title") });
				}
			}

			if (context?.previousUserSnapShot) {
				queryClient.setQueryData([QueryKeys.User], context.previousUserSnapShot);
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [QueryKeys.User] });
		},
		onSettled: async (_, error, values, context) => {
			if (error) return; // onError already handled it

			// Wait for refetch to complete
			await queryClient.refetchQueries({ queryKey: [QueryKeys.User] });
			const updatedUser = queryClient.getQueryData<User>([QueryKeys.User]);

			// Check if fields we tried to update are now null (validation failed on backend)
			const hasValidationFailures = Object.keys(values).some((key) => {
				const attemptedValue = values[key as keyof typeof values];
				const actualValue = updatedUser?.[key as keyof User];
				// If we tried to set a value but it's now null/undefined, backend validation failed
				return attemptedValue !== null && actualValue === null;
			});

			if (hasValidationFailures) {
				showToast({
					type: "error",
					title: t("error_toast_title"),
					description: t("error_failed_user_update"),
					duration: 5000,
					distanceToBottom: safeArea.bottomAsPx(),
				});
			} else if (isEqual(updatedUser, context?.previousUserSnapShot)) {
				showToast({
					type: "error",
					title: t("error_toast_title"),
					description: t("error_failed_user_update"),
					duration: 5000,
					distanceToBottom: safeArea.bottomAsPx(),
				});
			} else {
				// Only show success if validation passed
				showToast({
					type: "success",
					description: t("profile_update_success"),
					distanceToBottom: safeArea.bottomAsPx(ThemeSpaceVariable.Medium),
				});
			}
		},
	});

	const saveUser = (values: Partial<User>) => {
		if (!values) return;
		//we can validate forms but this is just insurance so we actually send information as we want it.
		const validationResult = validationSchema.safeParse(values);
		//handle this error somehow, render a toast or something
		if (!validationResult.success) {
			showToast({
				type: "error",
				title: t("message_error_title_save_user"),
				description: t("message_error_description"),
				distanceToBottom: safeArea.bottomAsPx(),
			});
		} else {
			mutation.mutate(values);
		}
	};

	const saveUserAsync = async (values: Partial<User>) => {
		if (!values) return false;
		//we can validate forms but this is just insurance so we actually send information as we want it.
		const validationResult = validationSchema.safeParse(values);
		if (!validationResult.success) {
			const relevantInfo = values.systemBankAccounts?.map((x) => {
				const clearingNumberType = typeof x.clearingNumber;
				const clearingNumberLength = clearingNumberType === "string" ? x.clearingNumber.length : 0;
				const accountNumberType = typeof x.accountNumber;
				const accountNumberLength = accountNumberType === "string" ? x.accountNumber.length : 0;
				const ibanAccountNumberType = typeof x.ibanAccountNumber;
				const ibanAccountNumberLength = ibanAccountNumberType === "string" ? x.ibanAccountNumber.length : 0;

				return {
					systemName: x.systemName,
					employeeId: x.employeeId,
					isoCountryCode: x.isoCountryCode,
					clearingNumber: { length: clearingNumberLength, type: clearingNumberType },
					accountNumber: { length: accountNumberLength, type: accountNumberType },
					ibanAccountNumber: { length: ibanAccountNumberLength, type: ibanAccountNumberType },
				};
			});
			const validationTracking = JSON.stringify(relevantInfo) + " " + JSON.stringify(validationResult.error);
			applicationInsights.trackException(new Error(validationTracking));
			showToast({
				type: "error",
				title: t("message_error_title_save_user"),
				description: t("message_error_description"),
				distanceToBottom: safeArea.bottomAsPx(),
			});
			return false;
		} else {
			await mutation.mutateAsync(values);
		}
		return true;
	};

	return { saveUser, saveUserAsync };
};
